import {Alert} from 'src/design/atoms/Alert';
import {Anchor} from 'src/design/atoms/Anchor';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Text} from 'src/design/atoms/Text';
import {useMediaQuery} from '@mantine/hooks';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowRight,
  IconCircleCheck,
  IconCircleX,
  IconListDetails,
  IconMapPinOff,
} from '@tabler/icons-react';
import React from 'react';
import {Link} from 'react-router-dom';
import type {InspectionResponse, SiteResponse} from 'src/api/ProactApiClient';
import {InspectionStateEnum} from 'src/api/ProactApiClient';
import {cadenceLabel} from 'src/app/inspections/schedules/inspectionScheduleHelpers';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {Button} from 'src/design/atoms/Button';
import {CompletionProgress} from 'src/design/atoms/CompletionProgress';
import {Pill} from 'src/design/atoms/Pill';
import {InlineStat} from 'src/design/molecules/InlineStat';
import {StatTile} from 'src/design/molecules/StatTile';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {useInspectionPermissions} from 'src/shared/auth/permissions/useInspectionPermissions';
import {SidebarLinkInspections} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {InspectionStateEnumPill} from 'src/shared/constants/PillConstants';
import {formatUtcCalendarDate} from 'src/shared/utils/formatters';

export interface InspectionRecordHeaderProps {
  inspection: InspectionResponse;
  site?: SiteResponse;
  isTransitioning: boolean;
  onSchedule: () => void;
  onReopenRequested: () => void;
  onViewRemaining: () => void;
}

export const InspectionRecordHeader: React.FC<InspectionRecordHeaderProps> = ({
  inspection,
  site,
  isTransitioning,
  onSchedule,
  onReopenRequested,
  onViewRemaining,
}) => {
  const perms = useInspectionPermissions();
  const isWide = useMediaQuery('(min-width: 48em)');

  const canSchedule =
    perms.update && inspection.state === InspectionStateEnum.Draft;
  const canReopen =
    perms.reopen && inspection.state === InspectionStateEnum.Completed;
  const isPreStart =
    inspection.state === InspectionStateEnum.Draft ||
    inspection.state === InspectionStateEnum.Scheduled;
  const isInProgress = inspection.state === InspectionStateEnum.InProgress;

  const enteredCount = inspection.scopedDeviceCount - inspection.remainingCount;

  const dueLabel = formatUtcCalendarDate(inspection.dueAt ?? undefined);

  // A soft-deleted schedule keeps its id but loses the cadence; the meta item
  // stays for provenance while the link is dropped.
  const scheduleCadence = inspection.inspectionScheduleCadence;
  const scheduleId = inspection.inspectionScheduleId;
  const scheduleLabel = scheduleCadence
    ? cadenceLabel({
        cadence: scheduleCadence,
        intervalDays: inspection.inspectionScheduleIntervalDays,
      })
    : 'Recurring';

  const addressLine = [site?.address, site?.city].filter(Boolean).join(', ');
  const sublineParts = [
    `Due ${dueLabel}`,
    inspection.clientName ? `Client: ${inspection.clientName}` : undefined,
    addressLine || undefined,
  ].filter(Boolean);

  const headerContent = (
    <PageHeader>
      <PageHeader.Title icon={SidebarLinkInspections.icon}>
        {inspection.siteName} — {inspection.inspectionTypeName}
      </PageHeader.Title>

      <PageHeader.Badges>
        <Pill {...InspectionStateEnumPill(inspection.state)} />
        <CompletionProgress
          completed={enteredCount}
          total={inspection.scopedDeviceCount}
        />
      </PageHeader.Badges>

      {scheduleId !== undefined && (
        <PageHeader.Meta>
          <PageHeader.MetaItem label="Schedule">
            {scheduleCadence !== undefined && perms.manageSchedules ? (
              <Anchor
                size="sm"
                component={Link}
                to={`${PathUtility.Inspections('schedules')}?schedule=${scheduleId}`}>
                {scheduleLabel}
              </Anchor>
            ) : (
              scheduleLabel
            )}
          </PageHeader.MetaItem>
        </PageHeader.Meta>
      )}

      <PageHeader.Actions>
        {canSchedule && isWide && (
          <PageHeader.Action
            variant="secondary"
            onClick={onSchedule}
            loading={isTransitioning}>
            Schedule
          </PageHeader.Action>
        )}
        {canReopen && (
          <PageHeader.Action
            variant="secondary"
            onClick={onReopenRequested}
            loading={isTransitioning}>
            Re-open
          </PageHeader.Action>
        )}
      </PageHeader.Actions>
    </PageHeader>
  );

  return (
    <>
      {isInProgress ? (
        <Box
          pos="sticky"
          top={0}
          pb="xs"
          style={{
            zIndex: 10,
            background: 'var(--mantine-color-body)',
          }}>
          {headerContent}
        </Box>
      ) : (
        headerContent
      )}

      {sublineParts.length > 0 && (
        <Text size="sm" c="dimmed">
          {sublineParts.join(' · ')}
        </Text>
      )}

      {isInProgress && inspection.remainingCount > 0 && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />}>
          <Group justify="space-between" wrap="wrap" gap="xs">
            <Text size="sm">
              {inspection.remainingCount} device
              {inspection.remainingCount === 1 ? '' : 's'} still need
              {inspection.remainingCount === 1 ? 's' : ''} a result
            </Text>
            <Text
              size="sm"
              fw={600}
              component="button"
              onClick={onViewRemaining}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}>
              <Center inline component="span">
                Filter to the {inspection.remainingCount} remaining
                <IconArrowRight
                  size={14}
                  stroke={1.5}
                  style={{marginLeft: 4}}
                />
              </Center>
            </Text>
          </Group>
        </Alert>
      )}

      {isPreStart ? (
        <SimpleGrid cols={{base: 2, sm: 4}}>
          <InlineStat label="Devices" value={inspection.scopedDeviceCount} />
          <InlineStat
            label="Buildings · Floors"
            value={`${inspection.buildingCount} · ${inspection.floorCount}`}
          />
          <InlineStat label="Due" value={dueLabel} />
          {inspection.inspectionTypeStandards && (
            <InlineStat
              label="Standards"
              value={inspection.inspectionTypeStandards}
            />
          )}
        </SimpleGrid>
      ) : (
        <SimpleGrid cols={{base: 2, sm: 5}}>
          <StatTile
            title="Pass"
            value={inspection.passCount}
            icon={<IconCircleCheck size={20} />}
            iconColor="green"
          />
          <StatTile
            title="Fail"
            value={inspection.failCount}
            icon={<IconCircleX size={20} />}
            iconColor="red"
          />
          <StatTile
            title="No Access"
            value={inspection.noAccessCount}
            icon={<IconMapPinOff size={20} />}
            iconColor="blue"
          />
          <StatTile
            title="Remaining"
            value={inspection.remainingCount}
            icon={<IconListDetails size={20} />}
            iconColor="gray"
          />
          <StatTile
            title="Deficiencies"
            value={`${inspection.deficiencyOpenCount} open · ${inspection.deficiencyResolvedCount} resolved`}
            icon={<IconAlertTriangle size={20} />}
            iconColor="orange"
          />
        </SimpleGrid>
      )}

      {canSchedule && !isWide && (
        <Button
          fullWidth
          variant="outline"
          onClick={onSchedule}
          loading={isTransitioning}>
          Schedule
        </Button>
      )}
    </>
  );
};
